import { useEffect, useState, useCallback } from "react";
import { ArchiveRestore, Trash2, Package } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { photoThumb } from "@/lib/photoUrl";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

/**
 * Full-page 回收站 view (2026-02 spec 「把回收站放在左面的菜单」).
 *
 * Access rules mirror the backend:
 *   • admin  → sees every trashed product, can restore OR permanently purge
 *   • export → sees only their own trashed products, can restore only
 *              (purge is admin-only for safety)
 */
export default function Trash() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products/trash");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const restore = async (itemNumber) => {
    try {
      await api.post(`/products/${encodeURIComponent(itemNumber)}/restore`);
      toast.success(`${t("products.restored")}: ${itemNumber}`);
      fetchTrash();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const purge = async (itemNumber) => {
    try {
      await api.delete(`/products/${encodeURIComponent(itemNumber)}/purge`);
      toast.success(`${t("products.purged")}: ${itemNumber}`);
      setConfirmPurge(null);
      fetchTrash();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fmtTime = (v) => (v ? new Date(v).toLocaleString(lang === "zh" ? "zh-CN" : "en-US") : "-");
  const daysLeft = (v) => {
    if (!v) return "";
    const remaining = 30 - Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
    return remaining > 0 ? `${remaining}d` : "-";
  };

  return (
    <div className="space-y-4" data-testid="trash-page">
      <div>
        <h1 className="font-heading text-base font-bold flex items-center gap-2">
          <Trash2 size={20} className="text-brand" /> {t("products.trash")}
        </h1>
        <p className="text-xs text-muted-foreground mt-1">{t("products.trashHint")}</p>
      </div>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table data-testid="trash-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase w-16">{t("products.photo")}</TableHead>
              <TableHead className="text-xs uppercase">{t("products.itemNumber")}</TableHead>
              <TableHead className="text-xs uppercase">{t("products.description")}</TableHead>
              <TableHead className="text-xs uppercase">{t("products.deletedAt")}</TableHead>
              <TableHead className="text-xs uppercase w-20">{t("products.daysLeft")}</TableHead>
              <TableHead className="text-xs uppercase text-right w-44">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="trash-empty-state">
                  {t("products.trashEmpty")}
                </TableCell>
              </TableRow>
            )}
            {items.map((p) => (
              <TableRow key={p.item_number} data-testid={`trash-row-${p.item_number}`} className="hover:bg-secondary/40">
                <TableCell className="p-1">
                  {p.photo_url ? (
                    <img src={photoThumb(p.photo_url, "sm")} alt="" className="w-10 h-10 object-contain bg-white border border-border rounded" />
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center text-muted-foreground"><Package size={16} /></div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm font-semibold">{p.item_number}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={p.description}>{p.description || "-"}</TableCell>
                <TableCell className="text-xs font-mono">{fmtTime(p.deleted_at)}</TableCell>
                <TableCell className="text-xs font-mono text-amber-700">{daysLeft(p.deleted_at)}</TableCell>
                <TableCell className="text-right p-1.5">
                  {confirmPurge === p.item_number ? (
                    <div className="flex justify-end items-center gap-1.5">
                      <span className="text-xs text-destructive">{t("products.purgeSure")}</span>
                      <Button size="sm" variant="destructive" className="h-7 rounded-md" onClick={() => purge(p.item_number)} data-testid={`confirm-purge-${p.item_number}`}>
                        {t("common.delete")}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-md" onClick={() => setConfirmPurge(null)}>
                        {t("common.cancel")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 rounded-md" onClick={() => restore(p.item_number)} data-testid={`restore-${p.item_number}`}>
                        <ArchiveRestore size={13} /> {t("products.restore")}
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 rounded-md text-destructive hover:text-destructive" onClick={() => setConfirmPurge(p.item_number)} data-testid={`purge-${p.item_number}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
